import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Typography, Divider, List, ListItem, ListItemText, IconButton, Paper, Collapse } from '@mui/material';
import { ArrowBack, Restore, History, ExpandMore, ExpandLess } from '@mui/icons-material';
import { testItemService } from '../services/testItemService';
import { TestItemVersion } from '../types/testItem';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

interface VersionData {
    name?: string;
    description?: string;
    type?: string;
    priority?: string;
    severity?: string;
    status?: string;
    components?: string[];
    labels?: string[];
    steps?: Array<{
        action: string;
        expected_result: string;
        test_data?: string;
        attachment_ids?: number[];
    }>;
    check_items?: Array<{
        description: string;
        is_checked?: boolean;
        attachment_ids?: number[];
    }>;
    preconditions?: string;
    postconditions?: string;
    test_data?: string[];
    attachment_ids?: number[];
    folder_id?: number | null;
    display_number?: string;
    estimated_time?: number;
    tags?: string[];
    [key: string]: any;
}

const TestItemVersions: React.FC = () => {
    const { t } = useTranslation('testItems');
    const { projectId, testItemId } = useParams<{ projectId: string; testItemId: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [versions, setVersions] = useState<TestItemVersion[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedVersions, setExpandedVersions] = useState<number[]>([]);

    useEffect(() => {
        const fetchVersions = async () => {
            try {
                if (projectId && testItemId) {
                    const data = await testItemService.getTestItemVersions(Number(projectId), Number(testItemId));
                    setVersions(data);
                    // Expand the first version by default
                    if (data.length > 0) {
                        setExpandedVersions([data[0].id]);
                    }
                }
            } catch (error) {
                enqueueSnackbar(t('versions.fetchError'), { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchVersions();
    }, [projectId, testItemId, enqueueSnackbar, t]);

    const handleRevert = async (versionId: number) => {
        try {
            if (projectId && testItemId) {
                await testItemService.revertToVersion(Number(projectId), Number(testItemId), versionId);
                enqueueSnackbar(t('versions.revertSuccess'), { variant: 'success' });
                navigate(`/projects/${projectId}/test-items/${testItemId}`);
            }
        } catch (error) {
            enqueueSnackbar(t('versions.revertError'), { variant: 'error' });
        }
    };

    const toggleVersion = (versionId: number) => {
        setExpandedVersions(prev =>
            prev.includes(versionId)
                ? prev.filter(id => id !== versionId)
                : [...prev, versionId]
        );
    };

    const getChanges = (version: TestItemVersion) => {
        const changes: { field: string; oldValue: any; newValue: any }[] = [];
        const data = version.data as VersionData;

        // Compare with previous version
        const currentIndex = versions.findIndex(v => v.id === version.id);
        const previousVersion = currentIndex < versions.length - 1 ? versions[currentIndex + 1] : null;
        const previousData = (previousVersion?.data || {}) as VersionData;

        const fields = [
            { key: 'name', label: t('fields.name') },
            { key: 'description', label: t('fields.description') },
            { key: 'type', label: t('fields.type') },
            { key: 'priority', label: t('fields.priority') },
            { key: 'severity', label: t('fields.severity') },
            { key: 'status', label: t('fields.status') },
            { key: 'components', label: t('fields.components') },
            { key: 'labels', label: t('fields.labels') },
            { key: 'preconditions', label: t('fields.preconditions') },
            { key: 'postconditions', label: t('fields.postconditions') },
            { key: 'test_data', label: t('fields.testData') },
            { key: 'folder_id', label: t('fields.folder') },
            { key: 'display_number', label: t('fields.displayNumber') },
            { key: 'estimated_time', label: t('fields.estimatedTime') },
            { key: 'tags', label: t('fields.tags') }
        ];

        fields.forEach(({ key, label }) => {
            const newValue = data[key];
            const oldValue = previousData[key];

            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                changes.push({
                    field: label,
                    oldValue: formatValue(oldValue),
                    newValue: formatValue(newValue)
                });
            }
        });

        // Handle steps
        if (data.steps && JSON.stringify(data.steps) !== JSON.stringify(previousData.steps)) {
            changes.push({
                field: t('fields.steps'),
                oldValue: formatSteps(previousData.steps),
                newValue: formatSteps(data.steps)
            });
        }

        // Handle check items
        if (data.check_items && JSON.stringify(data.check_items) !== JSON.stringify(previousData.check_items)) {
            changes.push({
                field: t('fields.checkItems'),
                oldValue: formatCheckItems(previousData.check_items),
                newValue: formatCheckItems(data.check_items)
            });
        }

        return changes;
    };

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) return '-';
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '-';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    };

    const formatSteps = (steps?: Array<{ action: string; expected_result: string }>): string => {
        if (!steps || steps.length === 0) return '-';
        return steps.map(step => `${step.action} -> ${step.expected_result}`).join('\n');
    };

    const formatCheckItems = (items?: Array<{ description: string; is_checked?: boolean }>): string => {
        if (!items || items.length === 0) return '-';
        return items.map(item => `${item.description} (${item.is_checked ? '✓' : '✗'})`).join('\n');
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>{t('common.loading')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" component="h1">
                    {t('versions.title')}
                </Typography>
            </Box>

            {versions.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>{t('versions.noVersions')}</Typography>
                </Paper>
            ) : (
                <List>
                    {versions.map((version, index) => (
                        <Card key={version.id} sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" component="h2">
                                            {t('versions.version')} {version.version}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('versions.createdBy')}: {version.created_by_id}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('versions.createdAt')}: {format(new Date(version.created_at), 'PPpp')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton onClick={() => toggleVersion(version.id)}>
                                            {expandedVersions.includes(version.id) ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                        {index > 0 && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<Restore />}
                                                onClick={() => handleRevert(version.id)}
                                            >
                                                {t('versions.revert')}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                <Collapse in={expandedVersions.includes(version.id)}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                        {t('versions.changes')}
                                    </Typography>
                                    <List>
                                        {getChanges(version).length > 0 ? (
                                            getChanges(version).map((change, idx) => (
                                                <ListItem key={idx}>
                                                    <ListItemText
                                                        primary={change.field}
                                                        secondary={
                                                            <Box component="span">
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ textDecoration: 'line-through', color: 'error.main', mr: 1 }}
                                                                >
                                                                    {change.oldValue}
                                                                </Typography>
                                                                <Typography component="span" color="success.main">
                                                                    {change.newValue}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            ))
                                        ) : (
                                            <ListItem>
                                                <ListItemText primary={t('versions.noChanges')} />
                                            </ListItem>
                                        )}
                                    </List>
                                </Collapse>
                            </CardContent>
                        </Card>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default TestItemVersions; 